import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import api, { formatApiError } from '../api';
import { INTERVENTION_TYPES, URGENCY_LEVELS } from '../constants';

const empty = {
  type: 'depannage', urgency: 'normale', description: '',
  address: '', city: '', postalCode: '', preferredDate: '', preferredSlot: '',
  contactName: '', contactPhone: '', accessInstructions: '', comment: '',
};

const ClientNewRequest = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.description.length < 5) { toast.error('Merci de décrire votre problème'); return; }
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') delete payload[k]; });
      const { data } = await api.post('/interventions', payload);
      toast.success(`Demande ${data.number} envoyée !`);
      navigate('/espace-client');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="client-new-request">
      <h1 className="text-xl font-bold text-neutral-900 mb-1">Nouvelle demande d'intervention</h1>
      <p className="text-neutral-500 mb-6">Décrivez votre besoin, nous vous recontactons rapidement.</p>

      <form onSubmit={submit} className="bg-white rounded-xl border border-neutral-200 p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Type de demande</Label>
            <Select value={form.type} onValueChange={(v) => set('type', v)}>
              <SelectTrigger data-testid="request-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(INTERVENTION_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Niveau d'urgence</Label>
            <Select value={form.urgency} onValueChange={(v) => set('urgency', v)}>
              <SelectTrigger data-testid="request-urgency"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(URGENCY_LEVELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Description du problème *</Label>
          <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={4}
            placeholder="Décrivez le problème rencontré..." required data-testid="request-description" />
        </div>

        <div>
          <Label>Adresse d'intervention</Label>
          <Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Laisser vide pour utiliser votre adresse" data-testid="request-address" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Ville</Label><Input value={form.city} onChange={(e) => set('city', e.target.value)} data-testid="request-city" /></div>
          <div><Label>Code postal</Label><Input value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} data-testid="request-postal" /></div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Date souhaitée</Label><Input type="date" value={form.preferredDate} onChange={(e) => set('preferredDate', e.target.value)} data-testid="request-date" /></div>
          <div>
            <Label>Créneau souhaité</Label>
            <Select value={form.preferredSlot || undefined} onValueChange={(v) => set('preferredSlot', v)}>
              <SelectTrigger data-testid="request-slot"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="matin">Matin</SelectItem>
                <SelectItem value="apres-midi">Après-midi</SelectItem>
                <SelectItem value="journee">Journée</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Personne à contacter sur place</Label><Input value={form.contactName} onChange={(e) => set('contactName', e.target.value)} data-testid="request-contact-name" /></div>
          <div><Label>Téléphone du contact</Label><Input value={form.contactPhone} onChange={(e) => set('contactPhone', e.target.value)} data-testid="request-contact-phone" /></div>
        </div>

        <div>
          <Label>Consignes d'accès</Label>
          <Textarea value={form.accessInstructions} onChange={(e) => set('accessInstructions', e.target.value)} rows={2}
            placeholder="Code, étage, interphone..." data-testid="request-access" />
        </div>

        <Button type="submit" disabled={loading} className="w-full bg-red-700 hover:bg-red-600 h-11" data-testid="submit-request-button">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Envoyer ma demande
        </Button>
      </form>
    </div>
  );
};

export default ClientNewRequest;
